<script setup lang="ts">

import { onMounted, onUnmounted, computed, watch } from "vue";
import { useRouter } from "vue-router";
import { useWindowSize } from "@vueuse/core";
import { usePresentation } from "../Types/Presentation";
import { useAuthentication } from "../Types/Authentication";
import { Backgrounds } from "../Types/Backgrounds";
import { MAX_WIDTH, MAX_HEIGHT, TABLET_MAX_HEIGHT, TABLET_MAX_WIDTH } from "../Types/Settings";
import { DefaultSections } from "../Types";

import Header from "../Components/Header.vue";
import Navigation from "../Components/Navigation.vue";
import PageCarousel from "../Components/PageCarousel.vue";
import PresentationAuthentication from "../Components/PresentationAuthentication.vue";
import NextSlide from "../Components/NextSlide.vue";
import MediaLightbox from "../Components/MediaLightbox.vue";

const router = useRouter();
const auth = useAuthentication();
const presentation = usePresentation();

const showNavigation = computed( () => [ DefaultSections.Start ].includes( presentation.active ) == false );

const { width, height } = useWindowSize();
const aspectRatio = computed( () => width.value / height.value );

const showBars = computed( () => {
    return ( width.value > height.value && aspectRatio.value < presentation.ratio ) || height.value > ( presentation.isTabletMode ? TABLET_MAX_HEIGHT : MAX_HEIGHT );
} );

const maxHeight = computed( () => Math.min( width.value > height.value ? width.value / presentation.ratio : height.value, ( presentation.isTabletMode ? TABLET_MAX_HEIGHT : MAX_HEIGHT ) ) );
const clipWidth = computed( () => width.value > ( presentation.isTabletMode ? TABLET_MAX_WIDTH : MAX_WIDTH ) );

// Hide podium when the presentation is running
onMounted( () => document.querySelector( 'body' )!.classList.add( 'hide-podium' ) );
onUnmounted( () => document.querySelector( 'body' )!.classList.remove( 'hide-podium' ) );

// Update the url query params when the section changes
watch( () => presentation.currentSection, value => 
{
    const query = value.name == DefaultSections.Start ? null : `?s=${value.name}`;
    router.push( router.currentRoute.value.path + ( query ?? '' ) );
});

</script>

<template>

    <div v-if="presentation"
         class="flex flex-col items-center justify-center tracking-wider overflow-hidden w-full h-full mx-auto
                max-h-[60%] lg:max-h-full
                transition-all duration-500"
         :class="[
                presentation.isReady ? 'opacity-100' : 'opacity-0',
                presentation.isTabletMode ? 'rounded-3xl' : ''
            ]"
         :style="{ 
            color: `${presentation.theme.textColor}`,
            maxHeight: `${showBars ? maxHeight : height}px`,
            maxWidth: `${clipWidth && presentation.isTabletMode ? TABLET_MAX_WIDTH : 2160 }px`,
            background: `linear-gradient(to bottom, ${presentation.theme.bgColorFrom}, ${presentation.theme.bgColorTo})`
        }">

        <div class="hidden absolute top-6">
            Images loaded {{ presentation.imagesLoaded }} of {{ presentation.imageCount }}
        </div>
        
        <!-- TITLE PAGE HORIZON -->
        <transition mode="out-in"
            enter-active-class="transition ease-out duration-300"
            enter-from-class="transform opacity-0"
            enter-to-class="transform opacity-100 "
            leave-active-class="transition ease-in duration-150"
            leave-from-class="transform opacity-100"
            leave-to-class="transform opacity-0">

            <div v-if="presentation.theme.showHorizonLine && presentation.backgroundImage != null"
                class="absolute left-0 top-0 w-full h-full
                       flex flex-col items-center justify-center
                       text-2xl lg:text-6xl font-semibold uppercase max-h-[60%] lg:max-h-full">
                <img :src="presentation.backgroundImage" class="w-full h-full object-cover"
                    :class="{
                        'opacity-[5%]': presentation.backgroundImage == Backgrounds.Two || presentation.backgroundImage == Backgrounds.Five,
                        'opacity-[10%]': presentation.backgroundImage != Backgrounds.Two && presentation.backgroundImage != Backgrounds.Five
                    }" />
            </div>

        </transition>

        <!-- CONTENT PAGE HORIZON -->
        <transition mode="out-in"
            enter-active-class="transition ease-out duration-500"
            enter-from-class="transform opacity-0 top-y-20"
            enter-to-class="transform opacity-100 top-y-0"
            leave-active-class="transition ease-in duration-150"
            leave-from-class="transform opacity-100 top-y-0"
            leave-to-class="transform opacity-0 top-y-20">

            <div v-if="presentation.theme.showHorizonLine && presentation.isContentPage"
                class="absolute left-0 w-full h-[30vh] -translate-y-16
                       bg-gradient-to-b from-transparent via-70% via-black/[25%] to-transparent"
            />      

        </transition>

        <div class="flex flex-col items-center overflow-hidden w-full h-full"
             :class="{ 'mx-auto': clipWidth }">
            
            <!-- MAIN PAGE -->
            <transition mode="out-in"
                enter-active-class="transition ease-out !duration-150"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in !duration-150"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95">

                <PageCarousel v-if="auth.isAuthenticated && presentation.isReady" 
                    class="duration-500 transition-all"
                    :class="presentation.showMedia ? 'scale-90 opacity-0' : ''" />
                <PresentationAuthentication v-else />

            </transition>

            <!-- VIDEO -->
            <transition mode="out-in"
                enter-active-class="transition ease-out duration-150 delay-300"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-150"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95">

                <MediaLightbox v-if="auth.isAuthenticated && presentation.showMedia" />

            </transition>

            <!-- SLIDES PAGE FADE -->
            <transition mode="out-in"
                enter-active-class="transition ease-out duration-500"
                enter-from-class="transform opacity-0 top-y-20"
                enter-to-class="transform opacity-100 top-y-0"
                leave-active-class="transition ease-in duration-150"
                leave-from-class="transform opacity-100 top-y-0"
                leave-to-class="transform opacity-0 top-y-20">

                <div v-if="presentation.active == DefaultSections.SlideSorter"
                    class="absolute left-0 w-full h-[8em] bottom-0 pointer-events-none"
                    :style="{ 
                        background: `linear-gradient( to bottom, transparent, rgb( from ${presentation.theme.bgColorFrom} r g b / 60%), rgb( from ${presentation.theme.bgColorFrom} r g b / 95%) )`
                    }"
                />      

            </transition>

            <!-- NAVIGATION PANEL -->
            <transition mode="out-in"
                enter-active-class="transition ease-out duration-500"
                enter-from-class="transform opacity-0 translate-y-full"
                enter-to-class="transform opacity-100 -translate-y-0"
                leave-active-class="transition ease-in duration-150"
                leave-from-class="transform opacity-100 -translate-y-0"
                leave-to-class="transform opacity-0 translate-y-full">

                <Navigation
                    v-if="showNavigation && presentation.showNavigation"
                    class="transition-all duration-300 absolute bottom-6 mx-auto"
                    :class="[
                        auth.isAuthenticated && showNavigation ? 'translate-y-0' : 'translate-y-full pt-4',
                        presentation.showMedia ? 'scale-90 opacity-0 pointer-events-none' : ''
                    ]" 
                />

            </transition>

        </div>

        <transition mode="out-in"
            enter-active-class="transition ease-out duration-500"
            enter-from-class="transform opacity-0 top-y-20"
            enter-to-class="transform opacity-100 top-y-0"
            leave-active-class="transition ease-in duration-150"
            leave-from-class="transform opacity-100 top-y-0"
            leave-to-class="transform opacity-0 top-y-20">

            <Header
                v-if="presentation.isReady"
                class="transition-all duration-300"
                :class="[
                    auth.isAuthenticated && showNavigation ? 'translate-y-0' : '-translate-y-full',
                    presentation.showMedia ? 'scale-90 opacity-0 pointer-events-none' : ''
                ]" 
            />

        </transition>

        <NextSlide 
            v-if="presentation.isReady && presentation.enableNextSlideIndicator"
            class="transition-all duration-300"
            :class="[
                presentation.showMedia ? 'scale-90 opacity-0 pointer-events-none' : ''
            ]"
        />
        
    </div>

</template>